import { useEffect, useMemo, useRef, useState } from 'react';

import { useAppSelector } from 'state/hooks';
import {
  selectBinSize,
  selectCapacityLanes,
  selectCapacityThreshold,
  selectCurrentProjectInfo,
  selectTypicalMonth,
} from 'state/workflowSlice';

import { BIN_60_MINS } from 'appConstants';
import dayjs from 'dayjs';
import { PlanningChartDataExpander } from 'features/chart/PlanningChartDataExpander';
import {
  makeFormatTooltip,
  makeVolumeAxisLabeller,
  planningChartOptions,
} from 'features/chart/planningChartOptions';
import Highcharts from 'highcharts';

import { usePlanningVolumesDataProvider } from '../workflow_planning/planningVolumesCommon';
import { SyncedHighchartsReact } from './SyncedHighchartsReact';
import { graph_highlight_color } from './chartOptions';
import { useChartResize } from './useChartResize';

import styles from './Chart.module.css';

function formatVolumeSeriesName(typicalMonth, has_volume_model) {
  return `${dayjs(typicalMonth, 'MMMM-YYYY').format("MMM 'YY")} ${
    has_volume_model ? 'volume' : 'trip count'
  }`;
}

export function PlanningChartVolume() {
  const capacityThreshold = useAppSelector(selectCapacityThreshold);
  const capacityLanes = useAppSelector(selectCapacityLanes);
  const binSize = useAppSelector(selectBinSize);
  const userProject = useAppSelector(selectCurrentProjectInfo);

  const typicalMonth = useAppSelector(selectTypicalMonth);

  // normalize capacityThreshold to binSize
  const binCapacityThreshold = useMemo(
    () => capacityThreshold * capacityLanes,
    [capacityThreshold, capacityLanes, binSize],
  );

  const chartComponent = useRef(null); // so we can call reflow

  const [chartOptions, setChartOptions] = useState(planningChartOptions as any);
  useChartResize(chartComponent);
  const planningVolumeData = usePlanningVolumesDataProvider();

  // memoize chartDataExpander
  const chartDataExpander = useMemo(
    () =>
      new PlanningChartDataExpander(
        binSize,
        userProject?.uses_middle_east_day_types,
      ),
    [binSize],
  );

  // memoize expanded segmentSpeeds
  const segmentVolumes = useMemo(
    () => chartDataExpander?.expandPlanningData(planningVolumeData),
    [planningVolumeData, chartDataExpander],
  );

  // memoize thresholdVolumeFlatline
  const thresholdCapacityFlatline = useMemo(
    () => chartDataExpander?.flatline(binCapacityThreshold),
    [binCapacityThreshold, chartDataExpander],
  );

  // memoize thresholdBands
  const thresholdBands = useMemo(
    () =>
      chartDataExpander?.volumeThresholdBands(
        segmentVolumes,
        binCapacityThreshold,
        graph_highlight_color,
      ),
    [segmentVolumes, binCapacityThreshold, chartDataExpander],
  );

  // update chart with new targetDate, freeflowSpeeds, segmentSpeeds
  useEffect(() => {
    setChartOptions({
      yAxis: {
        title: {
          text: userProject?.has_volume_model ? 'Hourly volume' : 'Trip count',
        },
      },
      xAxis: {
        plotBands: thresholdBands,
        labels: {
          formatter: makeVolumeAxisLabeller(
            userProject?.uses_middle_east_day_types,
          ),
        },
      },
      series: [
        {
          id: 'capacity_threshold_flatline',
          data: thresholdCapacityFlatline,
          marker: { enabled: false },
          name:
            binSize === BIN_60_MINS
              ? 'Capacity Threshold'
              : 'Capacity Threshold',
        },
        {
          id: 'typical_volume',
          data: segmentVolumes,
          marker: { enabled: false },
          name: formatVolumeSeriesName(
            typicalMonth,
            userProject?.has_volume_model,
          ),
        },
      ],
      tooltip: {
        formatter: makeFormatTooltip(
          parseFloat(binSize),
          thresholdBands,
          userProject?.uses_middle_east_day_types,
        ),
      },
    });
  }, [segmentVolumes, thresholdCapacityFlatline, binSize, thresholdBands]);

  return (
    <div className={styles.chart}>
      <SyncedHighchartsReact
        ref={chartComponent}
        highcharts={Highcharts}
        containerProps={{ style: { height: '100%', width: '100%' } }}
        options={chartOptions}
      />
    </div>
  );
}
